/**
 * HistoryConversationView - Conversa de HISTÓRICO de um par (contato, canal)
 *
 * Mesmo design do ChatBox (header + bolhas + separadores de data), porém
 * read-only: exibe a timeline de mensagens de TODAS as conversas fechadas
 * do par, intercaladas cronologicamente, com divisor por atendimento.
 *
 * NÃO modifica o ChatBox — reusa os componentes standalone: ChatMessage
 * (bolha completa, só precisa de message+isOwn) e DateSeparator.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ConversationMessageResponse } from '@troia-v3/types';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { PrivacyText } from '@/shared/components/ui/PrivacyText';
import { Icon, IntegrationIcon } from '@/shared/icons';
import { HEADER_HEIGHT } from '@/shared/constants/layout';
import { formatPhoneNumber } from '@/shared/utils';
import { useAuth } from '@/contexts/AuthContext';
import ChatMessage from './ChatMessage';
import DateSeparator, { isSameDay, getMessageDate } from './DateSeparator';
import { useContact } from '../hooks/useContact';
import { useConversationHistoryDetail } from '../hooks/useConversationHistoryDetail';
import { useInfiniteHistoryMessages } from '../hooks/useInfiniteHistoryMessages';
import { useUnblockContact } from '@/modules/contacts/hooks/useContactMutations';
import { usePermissions } from '@/hooks/usePermissions';

interface HistoryConversationViewProps {
  contactId: string;
  channelId: string;
}

export const HistoryConversationView: React.FC<HistoryConversationViewProps> = ({
  contactId,
  channelId,
}) => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const currentUserId = authState.user?.id;

  const { data: contact } = useContact(contactId);
  // Desbloquear a partir do Histórico (2026-09-01) — é onde a conversa do
  // contato bloqueado vai parar, e o menu de onde ele foi bloqueado já sumiu.
  const unblockMutation = useUnblockContact();
  // O Histórico abre com `contacts:read`, mas a rota de desbloqueio exige
  // `contacts:update` no servidor — sem este gate o botão aparece para quem só
  // lê e o clique não faz nada. Mesma permissão do kebab do ChatBox.
  const { hasModulePermission } = usePermissions();
  const canUpdateContacts = hasModulePermission('contacts', 'update');
  // Metadados das conversas do par: contagem, provider (ícone do canal) e
  // datas por conversationId (rótulo dos divisores de atendimento)
  const { data: conversationsData } = useConversationHistoryDetail(contactId, channelId, { page: 1, limit: 100 });

  const {
    data: messagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: messagesLoading,
  } = useInfiniteHistoryMessages(contactId, channelId);

  // Backend devolve sentAt desc (página 1 = mais recentes) → reverte pra
  // ordem cronológica, igual ao ChatBox
  const displayMessages = useMemo(() => {
    const flat = messagesData?.pages.flatMap((page) => page.items) ?? [];
    return [...flat].reverse();
  }, [messagesData]);

  const conversationInfoMap = useMemo(() => {
    const map = new Map<string, { createdAt: string; closedAt?: string }>();
    for (const conv of conversationsData?.items ?? []) {
      map.set(conv.id, { createdAt: conv.createdAt, ...(conv.closedAt ? { closedAt: conv.closedAt } : {}) });
    }
    return map;
  }, [conversationsData]);

  const latestConversation = conversationsData?.items?.[0];
  const displayName = contact?.name || latestConversation?.contact?.name || 'Contato';
  const avatarUrl = contact?.picture || latestConversation?.contact?.picture;
  const phone = contact?.identifiers?.phone?.[0] || latestConversation?.contact?.phone || undefined;
  const providerType = latestConversation?.provider?.type;
  const providerName = latestConversation?.provider?.name;
  const totalConversations = conversationsData?.total ?? 0;

  // Scroll pro fim (mensagens mais recentes) no carregamento inicial;
  // carregar páginas antigas NÃO re-desce o scroll
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const didInitialScrollRef = useRef(false);
  useEffect(() => {
    if (!didInitialScrollRef.current && displayMessages.length > 0) {
      messagesEndRef.current?.scrollIntoView();
      didInitialScrollRef.current = true;
    }
  }, [displayMessages.length]);

  const formatConversationDate = (iso?: string): string => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div
      className="flex-1 flex flex-col relative h-full overflow-hidden"
      style={{ backgroundColor: 'var(--theme-card-background)' }}
      data-testid="history-conversation-view"
    >
      {/* HEADER — mesmo visual do ChatBox */}
      <div
        className="border-b px-6 flex items-center justify-between flex-shrink-0"
        style={{ borderColor: 'var(--theme-card-border)', height: HEADER_HEIGHT, minHeight: HEADER_HEIGHT }}
      >
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <Avatar className="w-10 h-10" isSquircle>
              <AvatarImage src={avatarUrl} alt={displayName} isSquircle />
              <AvatarFallback isSquircle>
                <span className="text-indigo-600 font-semibold">
                  {displayName.charAt(0) || 'C'}
                </span>
              </AvatarFallback>
            </Avatar>
            {providerType && (
              <div
                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
                title={providerName || providerType}
              >
                <IntegrationIcon id={providerType} size={12} variant="brand" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold flex items-center" style={{ color: 'var(--theme-card-text)' }}>
              {displayName}
              <span
                className="inline-flex items-center gap-1 ml-2 text-xs px-2 py-0.5 rounded-full"
                style={{ backgroundColor: 'var(--theme-button-ghost-hover)', color: 'var(--theme-muted-text)' }}
                title="Histórico de atendimentos finalizados — somente leitura"
                data-testid="history-conversation-badge"
              >
                <Icon name="clock" className="w-3 h-3" />
                Histórico
              </span>
            </h3>
            {/* nowrap + overflow: em 56px a sublinha NÃO pode quebrar em 2 linhas */}
            <div className="flex items-center gap-2 flex-nowrap overflow-hidden min-w-0">
              {phone && (
                <PrivacyText className="text-sm truncate" style={{ color: 'var(--theme-muted-text)' }}>
                  {formatPhoneNumber(phone)}
                </PrivacyText>
              )}
              {totalConversations > 0 && (
                <span className="text-sm whitespace-nowrap flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} data-testid="history-conversation-count">
                  {totalConversations} {totalConversations === 1 ? 'atendimento' : 'atendimentos'}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {contact?.blocked === true && canUpdateContacts && (
            <button
              type="button"
              onClick={() => unblockMutation.mutate(contact.id)}
              disabled={unblockMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 h-9 text-sm font-medium disabled:opacity-60"
              style={{ color: 'var(--theme-error-fg)', borderColor: 'var(--theme-input-border)' }}
              data-testid="history-contact-unblock"
            >
              <Icon name="unlock" className="w-4 h-4" />
              <span>Desbloquear</span>
            </button>
          )}

          <button
            onClick={() => navigate('/history')}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            title="Fechar"
            data-testid="history-conversation-close"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* MENSAGENS — mesmo container/loop do ChatBox */}
      <div
        data-testid="history-message-list"
        className="flex-1 overflow-y-auto p-6"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        {messagesLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div
                className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-2"
                style={{ borderColor: 'var(--theme-sidebar-active)' }}
              ></div>
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                Carregando histórico...
              </p>
            </div>
          </div>
        ) : displayMessages.length > 0 ? (
          <div className="space-y-2">
            {/* Carregar mensagens mais antigas (topo) */}
            {hasNextPage && (
              <div className="py-4 flex items-center justify-center">
                {isFetchingNextPage ? (
                  <div className="flex items-center gap-2" style={{ color: 'var(--theme-sidebar-active)' }}>
                    <div
                      className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                      style={{ borderColor: 'var(--theme-sidebar-active)' }}
                    ></div>
                    <span className="text-sm">Carregando mensagens antigas...</span>
                  </div>
                ) : (
                  <button
                    onClick={() => fetchNextPage()}
                    className="text-sm font-medium"
                    style={{ color: 'var(--theme-sidebar-active)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                    data-testid="history-load-older"
                  >
                    Carregar mensagens antigas
                  </button>
                )}
              </div>
            )}

            {displayMessages.map((message: ConversationMessageResponse, index: number) => {
              const messageDate = getMessageDate(message);
              const previousMessage = index > 0 ? displayMessages[index - 1] : null;
              const previousDate = previousMessage ? getMessageDate(previousMessage) : null;
              const showDateSeparator = index === 0 || (previousDate && !isSameDay(messageDate, previousDate));

              // Divisor de atendimento: quando muda a conversa de origem
              const showConversationDivider =
                index > 0 && previousMessage && previousMessage.conversationId !== message.conversationId;
              const conversationInfo = conversationInfoMap.get(message.conversationId);

              return (
                <div key={message.id}>
                  {showConversationDivider && (
                    <div className="flex items-center gap-3 py-4" data-testid={`history-conversation-divider-${message.conversationId}`}>
                      <div className="flex-1 border-t" style={{ borderColor: 'var(--theme-card-border)' }} />
                      <span
                        className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full flex-shrink-0"
                        style={{ backgroundColor: 'var(--theme-button-ghost-hover)', color: 'var(--theme-muted-text)' }}
                      >
                        <Icon name="message-square" className="w-3 h-3" />
                        Novo atendimento{conversationInfo ? ` · ${formatConversationDate(conversationInfo.createdAt)}` : ''}
                      </span>
                      <div className="flex-1 border-t" style={{ borderColor: 'var(--theme-card-border)' }} />
                    </div>
                  )}

                  {showDateSeparator && <DateSeparator date={messageDate} />}

                  <div id={`history-message-${message.id}`}>
                    <ChatMessage
                      message={message}
                      isOwn={message.direction === 'outbound'}
                      showAvatar={true}
                      currentUserId={currentUserId}
                    />
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full" data-testid="history-messages-empty">
            <div className="text-center">
              <Icon name="clock" className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--theme-muted-text)' }} />
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                Nenhuma mensagem no histórico deste contato neste canal
              </p>
            </div>
          </div>
        )}
      </div>

      {/* RODAPÉ — read-only, sem input */}
      <div
        className="border-t px-6 py-3 flex items-center justify-center gap-2 flex-shrink-0"
        style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-card-background)' }}
        data-testid="history-readonly-footer"
      >
        <Icon name="lock" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
        <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Histórico de atendimentos finalizados — somente leitura
        </span>
      </div>
    </div>
  );
};

export default HistoryConversationView;
